import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, CalendarClock, CalendarX2, Crown, Loader2, RefreshCw, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { SignInRequired } from "@/components/SignInRequired";
import { useAuth } from "@/hooks/useAuth";
import { storeLabel } from "@/lib/store-billing";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/premium-status")({
  component: PremiumStatusPage,
  head: () => ({
    meta: [
      { title: "My Premium Status — Inquira" },
      {
        name: "description",
        content:
          "See when your Inquira Premium started, the period you are paying for, when it renews, and any cancellation date.",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "My Premium Status — Inquira" },
      {
        property: "og:description",
        content: "Purchase date, current subscription period, renewal date and cancellation date for Inquira Premium.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "My Premium Status — Inquira" },
      { name: "twitter:description", content: "Track your Inquira Premium dates in one place." },
    ],
  }),
});

const fmtDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString(undefined, { dateStyle: "long" }) : null;

function Card({
  icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone?: "danger";
}) {
  return (
    <div className={`rounded-2xl border p-4 ${tone === "danger" ? "border-destructive/40 bg-destructive/5" : "border-rule bg-card"}`}>
      <p className="flex items-center gap-1.5 text-xs text-ink-muted">
        {icon} {label}
      </p>
      <p className="mt-1 break-words font-[family-name:var(--font-display)] text-lg font-extrabold text-ink">{value}</p>
      {hint && <p className="mt-1 text-[11px] leading-snug text-ink-muted">{hint}</p>}
    </div>
  );
}

function PremiumStatusPage() {
  const { user, isPaid, premium, refreshPremium, signOut } = useAuth();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void refreshPremium();
  }, [refreshPremium]);

  if (!user) {
    return (
      <SignInRequired
        title={i18n.t("edu.premium_status.my_premium_status")}
        description="Sign in to see when your Premium started, what period you're on and when it renews."
      />
    );
  }

  const refresh = async () => {
    setRefreshing(true);
    try {
      await refreshPremium();
    } finally {
      setRefreshing(false);
    }
  };

  const purchased = fmtDate(premium?.subscription_started_at ?? premium?.trial_started_at);
  const periodEnd =
    fmtDate(premium?.store_expires_at ?? premium?.current_period_end ?? premium?.subscription_expires_at ?? premium?.trial_ends_at);
  const cancelledOn = fmtDate(premium?.subscription_cancelled_at);
  const graceEnds = fmtDate(premium?.grace_period_ends_at);
  const willCancel = Boolean(premium?.cancel_at_period_end);
  const platform = premium?.store_platform ?? premium?.billing_platform;

  return (
    <div className="min-h-screen bg-surface">
      <AppHeader user={user} isPaid={isPaid} onSignOut={() => void signOut()} />

      <main className="mx-auto w-full max-w-2xl space-y-4 px-4 py-6">
        <Link to="/subscription" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink">
          <ArrowLeft className="size-4" /> <T k="edu.premium_status.subscription" />
        </Link>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-[family-name:var(--font-display)] text-2xl font-extrabold text-ink"><T k="edu.premium_status.my_premium_status" /></h1>
          <Button size="sm" variant="outline" onClick={() => void refresh()} disabled={refreshing}>
            {refreshing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            Refresh
          </Button>
        </div>

        <section className="rounded-2xl border border-rule bg-card p-5">
          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-neon-pink to-neon-yellow">
              <Crown className="size-5 text-white" />
            </span>
            <div className="min-w-0">
              <p className="font-[family-name:var(--font-display)] font-extrabold text-ink">
                {isPaid ? (premium?.plan_name ?? "Inquira Premium") : "Free plan"}
              </p>
              <p className="text-xs text-ink-muted">
                {isPaid
                  ? willCancel
                    ? "Active until the end of the period you already paid for"
                    : "Active and renewing"
                  : "No active Premium subscription"}
                {isPaid && platform ? ` · billed through ${storeLabel(platform as never)}` : ""}
              </p>
            </div>
          </div>
        </section>

        <div className="grid gap-3 sm:grid-cols-2">
          <Card
            icon={<ShoppingBag className="size-3.5" />}
            label={i18n.t("edu.premium_status.purchase_date")}
            value={purchased ?? "—"}
            hint={purchased ? "The day your Premium started." : "You haven't purchased Premium yet."}
          />
          <Card
            icon={<CalendarClock className="size-3.5" />}
            label={willCancel ? "Access ends" : "Current period ends"}
            value={periodEnd ?? "—"}
            hint={
              periodEnd
                ? willCancel
                  ? "Premium stays on until this date, then stops. No further charges."
                  : "Your subscription renews automatically on this date."
                : undefined
            }
          />
          <Card
            icon={<CalendarClock className="size-3.5" />}
            label={i18n.t("edu.premium_status.subscription_period")}
            value={purchased && periodEnd ? `${purchased} → ${periodEnd}` : purchased ?? "—"}
            hint={purchased && periodEnd ? "The span your Premium currently covers." : undefined}
          />
          <Card
            icon={<CalendarX2 className="size-3.5" />}
            label={i18n.t("edu.premium_status.cancellation_date")}
            value={cancelledOn ?? (willCancel ? "Cancellation scheduled" : "Not cancelled")}
            hint={
              cancelledOn
                ? "The day cancellation was requested."
                : willCancel
                  ? "Cancellation is set for the end of the current period."
                  : "Your subscription is not set to cancel."
            }
            tone={cancelledOn || willCancel ? "danger" : undefined}
          />
        </div>

        {graceEnds && (
          <p className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4 text-xs text-ink">
            We couldn't take your last payment. Premium stays on until {graceEnds} — update your payment method to keep
            it.
          </p>
        )}

        <section className="grid gap-2 sm:grid-cols-2">
          <Link to="/subscription">
            <Button variant="outline" size="full">
              Manage subscription
            </Button>
          </Link>
          <Link to="/billing">
            <Button variant="outline" size="full">
              Billing history
            </Button>
          </Link>
        </section>

        <p className="text-[11px] leading-relaxed text-ink-muted">
          All dates come from your payment provider (Apple, Google or card billing) — nothing is estimated on your
          device. If a date looks out of sync, tap Refresh.
        </p>
      </main>
    </div>
  );
}
